/**
 * Páginas legais — públicas.
 *
 * Sem sessão de propósito: quem ainda não tem conta precisa poder ler os termos
 * ANTES de aceitar. Exigir login para ler o contrato inverteria a ordem das
 * coisas, e é o tipo de detalhe que uma fiscalização nota.
 *
 * O texto vem do servidor já com VERSÃO e HASH. A tela mostra os dois: é o que
 * permite a alguém dizer "aceitei a versão 1.0, hash a3f…" e ser levado a sério.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { BRANDING } from '../config/branding';
import { SimboloCeres } from '../components/Marca';
import { ErrorState, LoadingRows } from '../components/ui';
import { TextoLegal } from '../components/TextoLegal';
import { RodapePublico } from '../components/RodapePublico';

interface Documento {
  chave: string;
  titulo: string;
  versao: string;
  hash: string;
  conteudo: string;
  idioma: string;
}

interface ItemDaLista {
  chave: string;
  titulo: string;
  versao: string;
  exigeAceite: boolean;
}

/**
 * `chaveFixa`: endereço curto e estável para um documento (`/privacidade`) —
 * o que se cadastra em painel de terceiros (ex.: Meta) e não pode mudar.
 */
export function LegalPage({ chaveFixa }: { chaveFixa?: string } = {}): JSX.Element {
  const { chave: chaveDaRota } = useParams<{ chave: string }>();
  const chave = chaveDaRota ?? chaveFixa;
  const [params, setParams] = useSearchParams();
  const idioma = params.get('idioma') === 'en' ? 'en' : 'pt-BR';

  const [doc, setDoc] = useState<Documento | null>(null);
  const [lista, setLista] = useState<ItemDaLista[]>([]);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    setDoc(null);
    setErro(null);
    const alvo = chave ?? 'termos-de-uso';
    void Promise.all([
      api.get<Documento>(`/legal/documentos/${alvo}?idioma=${idioma}`),
      api.get<{ data: ItemDaLista[] }>(`/legal/documentos?idioma=${idioma}`),
    ])
      .then(([d, l]) => {
        setDoc(d);
        setLista(l.data);
      })
      .catch(setErro);
  }, [chave, idioma]);

  const trocarIdioma = (novo: string): void => {
    const p = new URLSearchParams(params);
    p.set('idioma', novo);
    setParams(p);
  };

  return (
    <div className="min-h-screen bg-surface-sunken">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <SimboloCeres className="h-6 w-6 text-brand-600" decorativa />
            <span className="text-sm font-semibold text-ink">{BRANDING.productName}</span>
          </Link>
          {/* Troca de idioma como botões e não como select: dois valores não
              justificam um menu, e o estado fica visível sem abrir nada. */}
          <div className="flex gap-1" role="group" aria-label={t('legal.idiomaDocumento')}>
            {[
              ['pt-BR', 'Português'],
              ['en', 'English'],
            ].map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => trocarIdioma(valor!)}
                aria-pressed={idioma === valor}
                className={`rounded px-2 py-1 text-2xs font-medium ${
                  idioma === valor ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-4xl gap-6 px-4 py-6 md:grid-cols-[220px_1fr]">
        <nav aria-label={t('legal.documentos')}>
          <ul className="space-y-0.5">
            {lista.map((item) => (
              <li key={item.chave}>
                <Link
                  to={`/legal/${item.chave}?idioma=${idioma}`}
                  aria-current={item.chave === doc?.chave ? 'page' : undefined}
                  className={`block rounded px-2 py-1.5 text-xs ${
                    item.chave === doc?.chave
                      ? 'bg-brand-50 font-medium text-brand-700'
                      : 'text-ink-muted hover:bg-surface hover:text-ink'
                  }`}
                >
                  {item.titulo}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <main className="rounded-lg border border-line bg-surface p-5">
          {erro !== null ? (
            <ErrorState error={erro} />
          ) : doc === null ? (
            <LoadingRows rows={8} columns={1} />
          ) : (
            <article>
              <h1 className="text-xl font-semibold text-ink">{doc.titulo}</h1>
              {/* Versão e hash visíveis: é o que permite a alguém provar depois
                  qual texto leu. Escondê-los num rodapé seria escondê-los. */}
              <p className="mt-1 font-mono text-2xs text-ink-subtle">
                versão {doc.versao} · hash {doc.hash.slice(0, 16)}…
              </p>
              <div className="mt-4">
                <TextoLegal markdown={doc.conteudo} />
              </div>
            </article>
          )}
        </main>
      </div>
      <div className="mx-auto max-w-4xl px-4 pb-6">
        <RodapePublico />
      </div>
    </div>
  );
}
